import { useState, useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { PosCustomButton } from "../../../../customComponents/PosCustomButton/PosCustomButton";
import CustomTableGrid from "../../../../customComponents/CustomTableGrid/CustomTableGrid";
import CustomFilterComponent from "../../../../customComponents/CustomFilterComponent";
import {
  BuildingStorefrontIcon,
  EyeIcon,
  CalendarDaysIcon,
  ClockIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import "../index.css";
import "./history-kitchenOS.css";
import { MOCK_HISTORY_RECORDS, MOCK_LOCATION_OPTIONS } from "../MOCK_DATA_KOS";

const FILTER_CONFIG = [
  {
    key: "location",
    label: "Location",
    options: MOCK_LOCATION_OPTIONS,
  },
  {
    key: "status",
    label: "Status",
    options: [
      { label: "Approved", value: "Approved" },
      { label: "Partial", value: "Partial" },
      { label: "Rejected", value: "Rejected" },
    ],
  },
  {
    key: "prepSlot",
    label: "Prep Slot",
    options: [
      { label: "Morning", value: "Morning" },
      { label: "Afternoon", value: "Afternoon" },
      { label: "Evening", value: "Evening" },
    ],
  },
];

const EMPTY_FILTERS = { location: [], status: [], prepSlot: [] };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(date) {
  if (!date) return "";
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

// ─── Badge Helpers ────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  return (
    <span className={`h_kos_status-badge h_kos_status-${status.toLowerCase()}`}>
      {status}
    </span>
  );
}
StatusBadge.propTypes = { status: PropTypes.string.isRequired };

// ─── Column Definitions ───────────────────────────────────────────────────────

const HISTORY_COLUMNS = [
  {
    key: "id",
    label: "REQUEST ID",
    render: (row) => <span className="h_kos_id-link">{row.id}</span>,
  },
  {
    key: "outlet",
    label: "OUTLET (LOCATION)",
    render: (row) => (
      <span className="h_kos_outlet-cell">
        <BuildingStorefrontIcon className="h_kos_outlet-icon" />
        {row.outlet}
      </span>
    ),
  },
  {
    key: "itemCount",
    label: "ITEMS",
    render: (row) => `${row.itemCount} items`,
  },
  {
    key: "totalQty",
    label: "TOTAL QTY",
  },
  {
    key: "status",
    label: "STATUS",
    render: (row) => <StatusBadge status={row.status} />,
  },
  {
    key: "requestedDate",
    label: "REQUESTED DATE",
  },
  {
    key: "lastUpdated",
    label: "LAST UPDATED",
  },
  {
    key: "action",
    label: "ACTION",
    render: () => (
      <button className="h_kos_action-link">
        <EyeIcon className="h_kos_action-icon" />
        View
      </button>
    ),
  },
];

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function HistoryKitchenOS() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(EMPTY_FILTERS);
  const [dateRange, setDateRange] = useState([null, null]);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const dateRef = useRef(null);

  const [startDate, endDate] = dateRange;

  const activeFilterCount = useMemo(
    () => Object?.values(selectedFilters)?.reduce((sum, arr) => sum + arr?.length, 0),
    [selectedFilters]
  );

  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return MOCK_HISTORY_RECORDS?.filter((row) => {
      if (term && !row.id.toLowerCase()?.includes(term) && !row.outlet.toLowerCase()?.includes(term)) return false;
      if (selectedFilters.location?.length > 0 && !selectedFilters.location?.includes(row.outlet)) return false;
      if (selectedFilters.status?.length > 0 && !selectedFilters.status?.includes(row.status)) return false;
      return true;
    });
  }, [searchTerm, selectedFilters]);

  useEffect(() => {
    if (!datePickerOpen) return;
    function handleClickOutside(e) {
      if (dateRef.current && !dateRef.current.contains(e.target)) {
        setDatePickerOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [datePickerOpen]);

  const formattedRange =
    startDate && endDate
      ? `${formatDate(startDate)} - ${formatDate(endDate)}`
      : startDate
      ? `${formatDate(startDate)} - ...`
      : "Select date range";

  return (
    <Navbar pageTitle="Kitchen OS">
      <div className="idx_kos_wrapper">
        <div className="h_kos_page">
          {/* Header */}
          <div className="h_kos_header">
            <div className="h_kos_header-title-row">
              <ClockIcon className="h_kos_header-icon" />
              <p className="h_kos_title">History</p>
            </div>
            <p className="h_kos_subtitle">View past prep requests, approvals, and rejections.</p>
          </div>

          {/* Toolbar */}
          <div className="h_kos_toolbar">
            <div className="h_kos_search-wrapper">
              <MagnifyingGlassIcon className="h_kos_search-icon" />
              <input
                className="h_kos_search"
                placeholder="Search by PO, Item, or Outlet..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="h_kos_toolbar-right">
              <div className="h_kos_filter-btn-wrapper">
                <PosCustomButton
                  title="Filter"
                  variant="black-s"
                  p="7px 14px"
                  fontSize="13px"
                  border
                  onClickFunction={() => setFilterOpen(true)}
                />
                {activeFilterCount > 0 && (
                  <span className="h_kos_filter-badge">{activeFilterCount}</span>
                )}
              </div>
              <div className="h_kos_date-btn-wrapper" ref={dateRef}>
                <button
                  className="h_kos_date-btn"
                  onClick={() => setDatePickerOpen((prev) => !prev)}
                >
                  <CalendarDaysIcon className="h_kos_date-icon" />
                  {formattedRange}
                </button>
                {datePickerOpen && (
                  <div className="h_kos_date-popup">
                    <DatePicker
                      selectsRange
                      inline
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(range) => {
                        setDateRange(range);
                        if (range[0] && range[1]) setDatePickerOpen(false);
                      }}
                    />
                    <div className="h_kos_date-popup-actions">
                      <button
                        className="h_kos_date-clear"
                        onClick={() => {
                          setDateRange([null, null]);
                          setDatePickerOpen(false);
                        }}
                      >
                        Clear
                      </button>
                      <button
                        className="h_kos_date-ok"
                        onClick={() => setDatePickerOpen(false)}
                      >
                        OK
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="h_kos_table-card">
            <div className="h_kos_table-scroll">
              <CustomTableGrid
                columns={HISTORY_COLUMNS}
                rows={filteredData}
                rowKey="id"
                gridTemplateColumns="140px minmax(170px,1fr) 100px 110px 130px 190px 190px 110px"
              />
            </div>
            {filteredData?.length === 0 && (
              <p className="h_kos_no-results">No records match the current filters.</p>
            )}
          </div>
        </div>
      </div>

      <CustomFilterComponent
        open={filterOpen}
        handleClose={() => setFilterOpen(false)}
        filterConfig={FILTER_CONFIG}
        selectedFilters={selectedFilters}
        onApplyFilter={setSelectedFilters}
        variant="panel"
      />
    </Navbar>
  );
}
